import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import * as api from '@/store/api';
import { Vue } from 'vue-property-decorator';
import user from '@/store/modules/user';
import { normalizeSlides } from '@/components/simpleSlide/contentTypes';

export interface NextStepStarredItem {
  collectionId: string;
  leafId: string;
  name: string;
}

function findNextStepsSlideIndex(slides: any[]): number {
  return slides.findIndex((slide) => slide.slideType === 'contentNextSteps');
}

function findLiabilityIndex(slides: any[]): number {
  return slides.findIndex((slide) => slide.slideType === 'contentLiability');
}

function isContentTreeSlide(slide: any): boolean {
  return (
    !!slide &&
    Array.isArray(slide.subSlides) &&
    slide.subSlides.some((sub: any) => sub && sub.contentType === 'contentTree')
  );
}

function hasContentTreeSlide(slides: any[]): boolean {
  return slides.some(isContentTreeSlide);
}

/** Insert before Ansvarsbegränsning; prefer right after Våra tjänster / current slide. */
function findInsertIndexAfterServices(
  slides: any[],
  currentSlide: number,
): number {
  const liabilityIndex = findLiabilityIndex(slides);
  const maxInsert = liabilityIndex >= 0 ? liabilityIndex : slides.length;

  const currentIndex = currentSlide - 1;
  if (
    currentIndex >= 0 &&
    currentIndex < slides.length &&
    isContentTreeSlide(slides[currentIndex])
  ) {
    return Math.min(currentIndex + 1, maxInsert);
  }
  const servicesIndex = slides.findIndex(isContentTreeSlide);
  if (servicesIndex >= 0) {
    return Math.min(servicesIndex + 1, maxInsert);
  }
  // contentTree is often set on a local slide copy before the store updates —
  // place Nästa steg after the current slide, still before liability.
  if (currentIndex >= 0 && currentIndex < slides.length) {
    return Math.min(currentIndex + 1, maxInsert);
  }
  return maxInsert;
}

function ensureLiabilityIsLast(slides: any[]): void {
  const liabilityIndex = findLiabilityIndex(slides);
  if (liabilityIndex < 0 || liabilityIndex === slides.length - 1) {
    return;
  }
  const [liability] = slides.splice(liabilityIndex, 1);
  slides.push(liability);
}

function removeNextStepsFromState(state: {
  Slides: any[];
  NextStepsStarred: NextStepStarredItem[];
}): void {
  state.Slides = state.Slides.filter(
    (slide) => slide.slideType !== 'contentNextSteps',
  );
  state.NextStepsStarred = [];
}

function syncNextStepsWithContentTree(state: {
  Slides: any[];
  NextStepsStarred: NextStepStarredItem[];
}): void {
  if (!hasContentTreeSlide(state.Slides)) {
    removeNextStepsFromState(state);
  }
}

function readStarredFromSlides(slides: any[]): NextStepStarredItem[] {
  const index = findNextStepsSlideIndex(slides);
  if (index < 0) {
    return [];
  }
  return slides[index].starred || [];
}

function mergeStarredSources(
  slides: any[],
  docStarred?: NextStepStarredItem[],
): NextStepStarredItem[] {
  const fromSlide = readStarredFromSlides(slides);
  if (fromSlide.length > 0) {
    return fromSlide;
  }
  return docStarred || [];
}

// Plain helper so mutations can apply starred state directly. Calling one
// @Mutation from inside another via `this.` throws ERR_ACTION_ACCESS_UNDEFINED
// in vuex-module-decorators, which aborts loadFull and leaves the editor blank.
function applyStarredToState(state: any, starred: NextStepStarredItem[]) {
  const nextStarred = starred.slice();
  const index = findNextStepsSlideIndex(state.Slides);
  if (index >= 0) {
    Vue.set(state.Slides[index], 'starred', nextStarred);
  }
  state.NextStepsStarred = nextStarred;
}

function hasCompanyOrgNr(orgNr: string): boolean {
  return orgNr !== '' && orgNr !== '00';
}

export async function createFromTemplate({
  type,
  name,
  userId,
  template,
  orgNr,
  sieAggVersion,
  basedOn,
}: {
  type: string;
  name: string;
  userId: string;
  version: number;
  template: any;
  orgNr: string;
  sieAggVersion: number;
  basedOn: string;
}): Promise<string> {
  delete template.DocumentId;

  let data = {};
  if (orgNr !== '00') {
    data = await getData({
      orgNr,
      sieAggVersion,
      internal: false,
      userId,
      version: user.version,
    });
  }

  template.Type = type || 'deck';
  template.Name = name;
  template.Template = 'deck-template';
  template.OrgNr = orgNr;
  template.SieAggVersion = sieAggVersion;
  template.BasedOn = basedOn;

  for (const slide of template.Slides) {
    if (!slide.subSlides) {
      continue;
    }
    if (slide.template && orgNr !== '00') {
      const updated = getSlideByTemplate({
        template: slide.template,
        orgNr,
        sieAggVersion,
        data,
      });
      Object.assign(slide, updated);
      continue;
    }
    for (const subSlide of slide.subSlides) {
      if (subSlide.OrgNr) {
        subSlide.OrgNr = orgNr === '00' ? '' : orgNr;
        subSlide.SieAggVersion = sieAggVersion;
      }
    }
  }

  return await api.addDocument(template);
}

async function getData({
  orgNr,
  sieAggVersion,
  internal,
  userId,
  version,
}: {
  orgNr: string;
  sieAggVersion: number;
  internal: boolean;
  userId: string;
  version: number;
}) {
  if (!hasCompanyOrgNr(orgNr)) {
    return {};
  }

  const quick = await api.getQuick(
    userId,
    version,
    orgNr,
    sieAggVersion,
    internal,
  );

  if (!quick) {
    return {};
  }

  const data: any = {};
  data.ranking = quick.ranking;
  data.profitloss = quick.profitloss;
  data.balance = quick.balance;
  data.cashflow = quick.cashflow;
  data.sustainability = quick.sustainability;
  data.overview = quick.overview;

  return data;
}

function closingPlanYear(now: Date = new Date()): number {
  // Spring close (Feb–May) belongs to the coming season: next calendar year from July.
  return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
}

function closingPlanDate(month: number, day: number, hour: number, minute: number): string {
  const year = closingPlanYear();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${year}-${p(month)}-${p(day)}T${p(hour)}:${p(minute)}:00.000Z`;
}

function getSlideByTemplate({
  template,
  orgNr,
  sieAggVersion,
  data,
}: {
  template: string;
  orgNr: string;
  sieAggVersion: number;
  data: any;
}) {
  let slide;
  switch (template) {
    case 'profitlossOverview':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentList',
            description: 'Resultatöversikt',
            items: [
              data.profitloss.sheets[0].A === 0
                ? 'Bolaget saknar omsättning'
                : 'Omsättning på {{pl-nettoomsattning}}',
              data.profitloss.count === 1
                ? 'Bolagets första redovisade bokslut'
                : data.profitloss.sheets[0].MONTHS === '12'
                ? 'Omsättningstillväxt {{kpi-omsattningstillvaxt}}'
                : 'Perioden avser ' +
                  data.profitloss.sheets[0].MONTHS +
                  ' månader',
              data.profitloss.sheets[0].K >= 0
                ? 'Rörelsemarginal på {{kpi-rorelsemarginal}}'
                : 'Negativ rörelsemarginal',
              '',
              '',
            ],
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'turnover',
            contentType: 'contentChart',
            description: 'Nettoomsättning',
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'operatingMargin',
            contentType: 'contentChart',
            description: 'Rörelsemarginal',
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'profitloss',
            contentType: 'contentChart',
            description: 'Senaste året',
          },
        ],
        comment: '',
      };
      break;
    case 'balanceOverview':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentList',
            description: 'Balansöversikt',
            items: [
              'Tillgångar på {{bs-summa-tillgangar}}',
              data.balance.sheets[0].M + 0.794 * (data.balance.sheets[0].N || 0) > 0
                ? 'Justerat eget kapital på {{bs-justerat-eget-kapital}}'
                : 'Negativt eget kapital',
              'Soliditet på {{kpi-soliditet}}',
              '',
              '',
            ],
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'totalAssets',
            contentType: 'contentChart',
            description: 'Balansomslutning',
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'solidity',
            contentType: 'contentChart',
            description: 'Soliditet',
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'balance',
            contentType: 'contentChart',
            description: 'Senaste året',
          },
        ],
        comment: '',
      };
      break;
    case 'liquidityOverview':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentList',
            description: 'Likviditetsöversikt',
            items: [
              data.balance.sheets[0].J > 0
                ? 'Kassa & bank på {{bs-kassa-bank}}'
                : 'Saknar kassa & bank',
              'Kassalikviditet på {{kpi-kassalikviditet}}',
              (data.balance.sheets[0].U || 0) + (data.balance.sheets[0].P || 0) > 0
                ? 'Räntebärande skulder på {{bs-rantebarande}}'
                : 'Inga räntebärande skulder',
              '',
              '',
            ],
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'cash',
            contentType: 'contentChart',
            description: 'Kassa & bank',
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'quickRatio',
            contentType: 'contentChart',
            description: 'Kassalikviditet',
          },
          {
            OrgNr: orgNr,
            SieAggVersion: sieAggVersion,
            chart: 'interestBearingLiabilities',
            contentType: 'contentChart',
            description: 'Räntebärande skulder',
          },
        ],
        comment: '',
      };
      break;
    case 'productQbis':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentImage',
            description: 'QBIS',
            staticType: 'qbis_start',
          },
          {
            contentType: 'contentImage',
            description: 'Time',
            staticType: 'qbis_time',
          },
          {
            contentType: 'contentImage',
            description: 'Project',
            staticType: 'qbis_project',
          },
          {
            contentType: 'contentImage',
            description: 'Expense',
            staticType: 'qbis_expense',
          },
        ],
        comment: '',
      };
      break;
    case 'productFlexHRM':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentLibrary',
            description: 'Flex HRM',
            contentCategory: 'flex_start',
            subjects: ['flex_start'],
          },
          {
            contentType: 'contentLibrary',
            description: 'Time',
            contentCategory: 'flex_time',
            subjects: [
              'flex_time_redovisning',
              'flex_time_overtid',
              'flex_time_franvaro',
              'flex_time_stampla',
            ],
          },
          {
            contentType: 'contentLibrary',
            description: 'Travel',
            contentCategory: 'flex_travel',
            subjects: [
              'flex_travel_kvitto',
              'flex_travel_bilresa',
              'flex_travel_kort',
            ],
          },
          {
            contentType: 'contentLibrary',
            description: 'Plan',
            contentCategory: 'flex_plan',
            subjects: [
              'flex_plan_autobemanning',
              'flex_plan_kalkylera',
              'flex_plan_pass',
            ],
          },
        ],
        comment: '',
      };
      break;
    case 'productAboutGT':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentImage',
            description: 'Om Grant Thornton',
            staticType: 'gt_about_us',
          },
          {
            contentType: 'contentImage',
            description: 'Våra kunder',
            staticType: 'gt_our_customers',
          },
          { description: '' },
          { description: '' },
        ],
        comment: '',
      };
      break;
    case 'productClimate':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentImage',
            description: 'Översikt',
            staticType: 'klimat_oversikt',
          },
          {
            contentType: 'contentImage',
            description: 'Enkel',
            staticType: 'klimat_enkel',
          },
          {
            contentType: 'contentImage',
            description: 'Utökad',
            staticType: 'klimat_utokad',
          },
        ],
        comment: '',
      };
      break;
    case 'productCertifiedAdvisor':
      slide = {
        slideType: 'slideContent',
        template,
        subSlides: [
          {
            contentType: 'contentImage',
            description: 'Aktivt styrelsearbete',
            staticType: 'radgivare_aktivtstyrelsearbete',
          },
          {
            contentType: 'contentImage',
            description: 'Budgetprocess',
            staticType: 'radgivare_budgetprocess',
          },
          {
            contentType: 'contentImage',
            description: 'CFO-coach',
            staticType: 'radgivare_cfocoach',
          },
          {
            contentType: 'contentImage',
            description: 'Hållbarhetsarbete',
            staticType: 'radgivare_hallbarhetsarbete',
          },
          {
            contentType: 'contentImage',
            description: 'Värdesimulering',
            staticType: 'radgivare_vardesimulering',
          },
          {
            contentType: 'contentImage',
            description: 'Ägardirektiv',
            staticType: 'radgivare_agardirektiv',
          },
        ],
        comment: '',
      };
      break;
  }

  return slide;
}

@Module({
  namespaced: true,
  name: 'deck',
  store,
  dynamic: true,
})
class DeckModule extends VuexModule {
  Type: string = 'deck';
  Template: string = '';
  DocumentId: string = '';
  UserId: string = '';
  AggVersion: number = 1000000000;
  OrgNr: string = '';
  Name: string = '';
  Title: string = '';
  Slides: any[] = [];
  OrgName: string = '';
  Language: string = '';
  Editable: boolean = true;

  SieAggVersion: number = 0;
  BasedOn: string = '';

  Background: string = 'dark';

  NextStepsStarred: Array<{
    collectionId: string;
    leafId: string;
    name: string;
  }> = [];

  Data: any = { version: 0 };
  IndustryData: any = { version: 0 };
  nav: any = { currentSlide: 1, currentSubSlide: 0 };

  original: string = '';
  isLoading: boolean = false;

  @Mutation
  reset() {
    Object.assign(this, { Type: 'deck' });
    Object.assign(this, { Template: '' });
    Object.assign(this, { DocumentId: '' });
    Object.assign(this, { UserId: user.user ? user.user.UserId : '' });
    Object.assign(this, { AggVersion: 1000000000 });
    Object.assign(this, { OrgNr: '' });
    Object.assign(this, { Name: '' });
    Object.assign(this, { Title: '' });
    Object.assign(this, { Slides: [] });
    Object.assign(this, { OrgName: '' });
    Object.assign(this, { Language: '' });
    Object.assign(this, { NextStepsStarred: [] });
  }

  @Action
  async create({
    type,
    orgNr,
    title,
    name,
    sieAggVersion,
    basedOn,
    langEN,
  }: {
    type: string;
    orgNr: string;
    name: string;
    title: string;
    sieAggVersion: number;
    basedOn: string;
    langEN: boolean;
  }): Promise<string> {
    return await this.createByTemplate({
      type,
      template: 'deck-empty',
      orgNr,
      name,
      title,
      sieAggVersion,
      basedOn,
      langEN,
    });
  }

  @Action
  async createByTemplate({
    type,
    template,
    orgNr,
    title,
    name,
    sieAggVersion,
    basedOn,
    langEN,
  }: {
    type: string;
    template: string;
    orgNr: string;
    name: string;
    title: string;
    sieAggVersion: number;
    basedOn: string;
    langEN: boolean;
  }): Promise<string> {
    this.reset();
    let data: any = {};
    if (orgNr !== '00') {
      await this.loadData({ orgNr, sieAggVersion, internal: false });
      data = this.Data[orgNr][sieAggVersion];
    }

    let slides;

    switch (template) {
      case 'deck-empty':
        slides = [
          {
            slideType: 'slideTitle',
            title,
            comment: '',
          },
          {
            slideType: 'contentLiability',
            comment: '',
          },
        ];
        break;
      case 'deck-yearendmeeting':
        slides = [
          {
            comment: '',
            customSubtitle: false,
            slideType: 'slideTitle',
            subtitle: '',
            title:
              (!langEN ? 'Bokslut ' : 'Fiscal year ') +
              data.balance.sheets[0].DATE_TO,
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: 'Agenda',
                items: [
                  !langEN ? 'Årsredovisning' : 'Annual report',
                  !langEN ? 'Flerårsjämförelse' : 'Multiple-year comparison',
                  !langEN
                    ? 'Nu blickar vi framåt...'
                    : 'Now looking forward...',
                  !langEN ? 'Feedback' : 'Feedback',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Årsredovisning' : 'Annual report',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                blobId: '',
                contentType: 'contentPdf',
                description: !langEN ? 'Utkast' : 'Draft',
                name: '',
              },
              {
                contentType: 'contentText',
                description: !langEN ? 'Signeringsprocess' : 'Signing process',
                text: !langEN
                  ? '1. Bokslutsbokningar\n\n2. Upprättad årsredovisning - Innehållet i år årsredovisningen är bestämt och klart för undertecknande\n\n3. Färdigställd årsredovisning - Hela styrelsen och VD undertecknar årsredovisningen\n\n4. Årsstämmoprotokoll ordförande, protokollförare\n\n5. Fastställelseintyg vid inlämning till Bolagsverket\n\n6. Inkomstdeklaration på Skatteverket'
                  : '1. Closing entries\n\n2. Prepared annual report - The content of the annual report is determined and ready for signing\n\n3. Completed annual report - The entire board and CEO sign the annual report\n\n4. Minutes of the annual general meeting chairman, secretary\n\n5. Confirmation certificate upon submission to the Companies Registration Office\n\n6. Income declaration at the Swedish Tax Agency',
                note:
                  'En årsredovisning anses upprättad den dag den är daterad. Den dagen är det slutliga innehållet bestämt och klart för undertecknande. Det kan vara den dag då årsredovisningen beslutades i styrelsen, men det kan också vara en annan dag då styrelseledamöterna och eventuell VD blev ense om årsredovisningens innehåll.\n\nKravet på datering kan uppfyllas genom att det i slutet av dokumentet anges att årsredovisningen har beslutats ett visst datum.\n\nEn årsredovisning anses färdigställd först när den både är daterad och undertecknad av samtliga styrelseledamöter och eventuell VD. Det ska framgå vilket datum respektive person har skrivit under.\n\nOm underskrift sker på papper och alla styrelseledamöter samt eventuell VD skriver under samma dag räcker det att datum för underskrift anges en gång. Är det däremot så att årsredovisningen behöver skickas runt postledes, och underteckningen således sker olika dagar, behöver datumet för respektive persons underskrift anges.\n\nKravet på uppgift om den dag då undertecknande skedde kan uppfyllas genom att datum för underskrift anges inte varje underskrift.\n\nNär årsredovisningen är daterad (=upprättad) får inga ändringar göras, det gäller både upplysningar i förvaltningsberättelsen och övriga delar av årsredovisningen.\n\nOm företaget behöver göra ändringar ska en ny årsredovisning tas fram, med ett nytt datum för när innehållet beslutats.\n\nUnderskrift bör ske i anslutning till upprättandet\n\nDet är en fördel att underteckna årsredovisningen i nära anslutning till den dag den daterats.\n\nOm det inträffar händelser av väsentlig betydels för företaget efter att årsredovisningen daterats, men innan alla har skrivit under, får styrelseledamöterna och eventuell VD ta ställning till om årsredovisningen behöver ändras.\n\nVad gäller om datering saknas?\nEftersom ändringen i årsredovisningslagen tillämpas för första gången kan det förekomma att någon datering utelämnats. Orsaken kan till exempel vara att årsredovisningsprogrammet inte uppdaterats eller att det använts en gammal mall för årsredovisning.',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Flerårsjämförelse' : 'Multiple-year comparison',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                chart: 'compass',
                contentType: 'contentChart',
                note:
                  'En bolagskompass är ett pedagogiskt sätt att visualisera hur ett företag mår där man i sin enklaste form kan säga att en stor figur visar ett bra bolag medans en liten figur visar ett dåligt bolag.\n\nDe i figuren redovisade nyckeltalen har valts med omsorg för att omfatta en så stor del av resultat- och balansräkningen som möjligt.\n\nDet indexerade värdet på de olika nyckeltalsaxlarna visar vilken percentil som bolaget når för det aktuella nyckeltalet i jämförelse med samtliga svenska aktiebolag.\n\nExempelvis innebär ett indexerat värde på 70 att bolagets aktuella nyckeltal är högre än 70% av samtliga svenska aktiebolag (och således sämre än de återstående 30 procenten).\n\nDe fyra huvudaxlarnas värden baseras huvudsakligen på närliggande nyckeltal utifrån en rätt komplicerad formel med syftet att i lekmannatermer skapa ett helhetsintryck av bolaget.',
                description: !langEN ? 'Bolagskompass' : 'Business navigator',
              },
              {
                chart: 'turnover',
                contentType: 'contentChart',
                description: !langEN ? 'Nettoomsättning' : 'Net sales',
              },
              {
                chart: 'operatingMargin',
                contentType: 'contentChart',
                description: !langEN ? 'Rörelsemarginal' : 'Operating margin',
              },
              {
                chart: 'equity',
                contentType: 'contentChart',
                description: !langEN ? 'Eget kapital' : 'Equity',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN
              ? 'Nu blickar vi framåt...'
              : 'Now looking forward...',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Fokusområden' : 'Focus areas',
                items: [
                  !langEN ? 'Utmaningar framöver?' : 'Challenges ahead?',
                  !langEN ? 'Uppsatta mål framöver?' : 'Goals set ahead?',
                  !langEN
                    ? 'Vilket stöd behöver ni?'
                    : 'What support do you need?',
                  '',
                  '',
                ],
              },
              {
                contentType: 'contentYearWheel',
                description: !langEN ? 'Årshjul' : 'Year wheel',
                text: '',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideFullContent',
            subSlides: [
              {
                contentType: 'contentTree',
                collectionId: 'grant-thornton-360',
                startNodeId: 'grant-thornton-360',
                description: 'Grant Thornton 360',
              },
            ],
          },
          {
            slideType: 'contentNextSteps',
            starred: [],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Feedback' : 'Feedback',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Feedback' : 'Feedback',
                items: [
                  !langEN
                    ? 'Är ni nöjda med samarbetet?'
                    : 'Are you satisfied with the cooperation?',
                  !langEN ? 'Vad kan vi förbättra?' : 'What can we improve?',
                  !langEN
                    ? 'Träffas vi lagom ofta?'
                    : 'Do we meet often enough?',
                  '',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'contentLiability',
          },
        ];
        break;
      case 'deck-startupmeeting':
        slides = [
          {
            comment: '',
            slideType: 'slideTitle',
            subtitle: '',
            title: !langEN ? 'Inför årsavslut' : 'Ahead of fiscal year closing',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: 'Agenda',
                items: [
                  !langEN ? 'Ekonomiskt nuläge' : 'Financial situation',
                  !langEN ? 'Planering' : 'Planning',
                  !langEN ? 'Hållbarhet' : 'Sustainability',
                  !langEN ? 'Övrigt' : 'Other',
                  !langEN ? 'Feedback' : 'Feedback',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Ekonomiskt nuläge' : 'Financial situation',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Översikt' : 'Overview',
                items: [
                  !langEN
                    ? 'Avstämt t.o.m. 202X-XX-XX'
                    : 'Balanced up to 202X-XX-XX',
                  !langEN
                    ? 'Nettoomsättning {{pl-nettoomsattning}}'
                    : 'Net sales {{pl-nettoomsattning}}',
                  !langEN
                    ? 'Rörelseresultat {{pl-rorelseresultat}}'
                    : 'Operating profit {{pl-rorelseresultat}}',
                  !langEN
                    ? 'Kassa & bank {{bs-kassa-bank}}'
                    : 'Cash {{bs-kassa-bank}}',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                chart: 'turnover',
                contentType: 'contentChart',
                description: !langEN ? 'Nettoomsättning' : 'Net sales',
              },
              {
                chart: 'operatingMargin',
                contentType: 'contentChart',
                description: !langEN ? 'Rörelsemarginal' : 'Operating margin',
              },
              {
                contentType: 'contentTable',
                description: !langEN ? 'Resultattabell' : 'Income table',
                table: 'profitloss',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                chart: 'balance',
                contentType: 'contentChart',
                description: !langEN ? 'Balansdiagram' : 'Balance chart',
              },
              {
                chart: 'cash',
                contentType: 'contentChart',
                description: !langEN ? 'Kassa & Bank' : 'Cash',
              },
              {
                chart: 'totalCashflow',
                contentType: 'contentChart',
                description: !langEN ? 'Totalt kassaflöde' : 'Total cash flow',
                note:
                  'Totalt kassaflöde är differensen mellan IB och UB för kassa och bank. Föreslå gärna kunden att göra en kassaflödesgenomgång för att dela upp och förstå kassaflödet bättre.',
              },
              {
                contentType: 'contentTable',
                description: !langEN ? 'Balanstabell' : 'Balance table',
                table: 'balance',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Planering' : 'Planning',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentSchedule',
                description: !langEN ? 'Tidplan' : 'Schedule',
                items: [
                  {
                    Text: '',
                    date: closingPlanDate(2, 15, 11, 18),
                    text: !langEN
                      ? 'Prel rapporter för året klara'
                      : 'Preliminary reports for the year ready',
                  },
                  {
                    date: closingPlanDate(3, 15, 11, 16),
                    text: !langEN
                      ? 'Bokslut till kund för godkännande'
                      : 'Annual report to customer for approval',
                  },
                  {
                    date: closingPlanDate(4, 1, 11, 16),
                    text: !langEN
                      ? 'Bokslut till revision'
                      : 'Annual report to audit',
                  },
                  {
                    date: closingPlanDate(5, 15, 11, 16),
                    text: !langEN ? 'Årsstämma' : 'Annual general meeting',
                  },
                  {
                    Text: '',
                    date: null,
                    text: '',
                  },
                  {
                    Text: '',
                    date: null,
                    text: '',
                  },
                  {
                    Text: '',
                    date: null,
                    text: '',
                  },
                ],
              },
              {
                contentType: 'contentList',
                description: !langEN
                  ? 'Punkter att beakta'
                  : 'Points to consider',
                items: [
                  !langEN
                    ? 'T.ex. Avsättning till P-fond'
                    : 'E.g. Provision to P-fund',
                  !langEN ? 'T.ex. Utdelning' : 'E.g. Dividend',
                  !langEN ? 'T.ex. Upplupna kostnader' : 'E.g. Accrued costs',
                  '',
                  '',
                ],
              },
              {
                contentType: 'contentText',
                description: !langEN ? 'Vem gör vad' : 'Who does what',
                text: !langEN
                  ? 'Ett förslag på vem som gör vad i processen'
                  : 'A proposal on who does what in the process',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Hållbarhet' : 'Sustainability',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentCategory: 'sustainability',
                contentType: 'contentLibrary',
                description: !langEN ? 'Förutsättningar' : 'Conditions',
                subjects: ['sustainabilityImpact2409'],
              },
              {
                contentCategory: 'sustainability',
                contentType: 'contentLibrary',
                description: !langEN ? 'Frågor' : 'Questions',
                subjects: ['sustainabilityQuestions'],
              },
              {
                contentCategory: 'sustainability',
                contentType: 'contentLibrary',
                description: !langEN ? 'Process' : 'Process',
                subjects: ['sustainabilityProcess'],
              },
              {
                contentType: 'contentImage',
                description: !langEN
                  ? 'Klimatnyckeltal'
                  : 'Climate key figures',
                staticType: !langEN
                  ? '250922_Klimatnyckeltal'
                  : '250922_Klimatnyckeltal_EN',
              },
              {
                contentType: 'contentImage',
                description: !langEN
                  ? 'Sociala nyckeltal'
                  : 'Social key figures',
                staticType: !langEN
                  ? '250922_Sociala_nyckeltal'
                  : '250922_Sociala_nyckeltal_EN',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Övrigt' : 'Other',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                blobId: '',
                contentType: 'contentPdf',
                description: 'GT Bridge',
                name: '2406_GT_bridge.pdf',
                staticType: !langEN ? '2406_GT_bridge' : '2406_GT_bridge_en',
              },
              {
                contentCategory: 'counseling',
                contentType: 'contentLibrary',
                description: !langEN ? 'K2/K3' : 'K2/K3',
                subjects: [
                  '251027_Uppdateringar_K2',
                  '251027_Ej_K2_Foretag',
                  '251027_Ej_K2_Mindre_Foretag',
                  '251027_Uppdateringar_K3',
                  '251027_Byt_K2_K3',
                ],
                note:
                  'Uppdateringar i K2-regelverket\nÄndringarna ska tillämpas första gången för de räkenskapsår som inleds närmast efter 31 december 2025.\n\nFöretag som inte längre får tillämpa K2\nEtt förtydligande gällande aktierelaterade ersättningar: Har man aktierelaterade ersättningar måsta man tillämpa K3. D.v.s. man kan inte vara kvar i K2 tills programmet löpt ut.\nBeträffande kryptotillgångar: gäller inte företag som endast någon enstaka gång har använt eller taget emot kryptotillgångar som betalningsmedel.\n\nMindre företag som inte längre får tillämpa K2.\nMindre företag 50/40/80\n Företag som har skatt som är väsentlig och som beror på att tillgångarnas redovisade värde eller på att avsättningars eller skulders redovisade värde är lägre än avsättningars och skulders skattemässiga värde (uppskjuten skatteskuld). Bedömningen ska baseras på redovisningen för det senaste räkenskapsåret för vilket det finns en färdigställd årsredovisning.\nOBS! Detta gäller inte bolag som inte har krav på revisor 3/1,5/3\n\nUppdateringar i K3-regelverket\nAvsättning för tilläggsköpeskilling redovisas enligt reglerna om avsättningar.\n\nÖvergån från K2 till K3\nDiskutera med kunden som har K2 idag. Är det bättre för kunden att gå över till K3?\n\nOm kunden träffas - boka gärna ett separat uppföljningsmöte.',
              },
              {
                contentCategory: 'counseling',
                contentType: 'contentLibrary',
                description: !langEN ? 'Budget' : 'Budget',
                subjects: !langEN ? ['budget'] : ['budget_en'],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideFullContent',
            subSlides: [
              {
                contentType: 'contentTree',
                collectionId: 'grant-thornton-360',
                startNodeId: 'grant-thornton-360',
                description: 'Grant Thornton 360',
              },
            ],
          },
          {
            slideType: 'contentNextSteps',
            starred: [],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Feedback' : 'Feedback',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Feedback' : 'Feedback',
                items: [
                  !langEN
                    ? 'Är ni nöjda med samarbetet?'
                    : 'Are you satisfied with the cooperation?',
                  !langEN ? 'Vad kan vi förbättra?' : 'What can we improve?',
                  !langEN
                    ? 'Träffas vi lagom ofta?'
                    : 'Do we meet often enough?',
                  '',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'contentLiability',
          },
        ];
        break;
      case 'deck-esgintroduction':
        slides = [
          {
            comment: '',
            slideType: 'slideTitle',
            subtitle: '',
            title: !langEN ? 'Hållbarhet' : 'Sustainability',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Agenda' : 'Agenda',
                note:
                  'Vi tänkte börja med en bakgrund kring regelverk och hur er omvärld kan påverka hur ert hållbarhetsarbete kan utformats.\n\nVi tänkte även berätta om hur ni via vårt rapporteringsverktyg kan föja ert koldioxidutsläpp samt utvecklingen av sociala nyckeltal månadsvis.\n\nSlutligen vill vi diskutera med er hur ni skulle kunna ta detta vidare i er verksamhet.',
                items: [
                  !langEN ? 'Bakgrund' : 'Background',
                  !langEN ? 'Er situation' : 'Your situation',
                  !langEN ? 'Klimatnyckeltal' : 'Climate key figures',
                  !langEN ? 'Sociala nyckeltal' : 'Social key figures',
                  !langEN ? 'Nästa steg' : 'Next step',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Bakgrund' : 'Background',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Vad är ESG?' : 'What is ESG?',
                note:
                  'ESG är ett bredare samlingsbegrepp för hållbarhetsfrågor som står för Environmental, Social och Governance. Inom begreppet ryms många hållbarhetsfrågor som berör företag, organisationer och samhället. \n\nUnder "E" finns frågor som rör klimatförändringar, NetZero, grön el, utsläppspolicyer etc. \n\nUnder "S" innefattas områden som jämställdhet, mångfald, mänskliga rättigheter, arbetsmiljö och jämlikhet. \n\nUnder "G" ryms ämnen inom bolagsstyrning som hållbarhetsredovisning, due diligence, affärsmodeller, strategier, transformationer, regelverksefterlevnad och visselblåsarsystem.',
                items: [
                  !langEN
                    ? 'Environmental (Miljö- och klimatfrågor)'
                    : 'Environmental (Environmental and climate issues)',
                  !langEN
                    ? 'Social (Sociala frågor)'
                    : 'Social (Social issues)',
                  !langEN
                    ? 'Governance (Styrningsfrågor)'
                    : 'Governance (Governance issues)',
                  '',
                  '',
                ],
              },
              {
                contentCategory: 'sustainability',
                contentType: 'contentLibrary',
                description: !langEN ? 'Intressenter' : 'Stakeholders',
                note:
                  'Det går att hoovra över cirklarna för att få uppslag i mötet om varför intressenterna kan vara viktiga för kunden',
                subjects: ['sustainabilityImpact2409'],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN ? 'ESRS-standarder' : 'ESRS standards',
                note:
                  'ESRS standarderna är själva regelverket. \n\nFöretag som omfattas av kravet kommer behöva rapportera sina strategier, affärsmodeller, styrning, organisation, väsentlighetsanalyser, påverkan, möjligheter, risker, policyer, mål, handlingsplaner och resultat.\n\nStandarden är uppbyggd i fyra tydliga delar:\n* Generell information om företagets hållbarhetsarbete, vilket inkluderar väsentlighetsanalys, affärsmodell och riskhantering\n* Miljö och klimat\n* Sociala frågor\n* Styrningsfrågor (Governance)\n\nSammantaget är det just nu\n* 12 Standarder\n* 1200 datapunkter = kvalitativ information\n EU ser över detta just nu (utifrån OMNIBUS) och datapunkterna föreslås minska med över 50%.\n\nBeskriver i detalj vilka uppgifter som ska redovisas och hur de ska redovisas. ESRS 2 är obligatorisk, resten avgörs av en väsentlighetsanalys. Det finns även möjlighet att redovisa bolagspecifik information som ligger utanför ESRS.',
                staticType: !langEN ? '250922_ESRS' : '250922_ESRS_EN',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'VSME' : 'VSME',
                note:
                  'VSME är en standard för små och medelstora bolag. Från EUs och EFRAGs håll hoppas man att denna frivilliga standard skall anammas av marknaden och blir praxis för de bolag som frivilligt vill börja arbeta med hållbarhetsfrågor. Fördelen med standarden är att det blir enhetligt och en naturlig resa mot att om bolaget växer, kunna fasa in sig i CSRD regelverket.\n\nVSME består av två moduler - Basic Module och Comprehensive Module. Comprehensive Module bygger vidare på Basic Module, vilket innebär att företag som väljer att rapportera enligt Comprehensive Module även måste uppfylla kraven i Basic Module.\n* Basic - Scoop 1 och 2\n* Comprehensive - Scoop 1, 2 och 3.',
                staticType: !langEN ? '250922_VSME' : '250922_VSME',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN ? 'Vår vision' : 'Our vision',
                note:
                  'Fundera inför mötet utifrån kundens bransch – vilka områden ser du kan vara relevanta för just den här kunden? \n\nBra inledande frågor är: \nVilka omvärldsfaktorer påverkar er just nu? \nHar ni identifierat era icke finansiella risker? \nVilka är era väsentliga frågor och har ni satt tydliga mål och nyckeltal även på icke-finansiella delar?.',
                staticType: !langEN ? 'gt_vision' : 'gt_vision_en',
              },
              {
                blobId: '',
                contentType: 'contentPdf',
                description: !langEN
                  ? 'Vår årsredovisning'
                  : 'Our annual report',
                note:
                  'Under "Vår årsredovisning" kan ni läsa hur vi på GT arbetar med hållbarhet - visa gärna från sidan 14 och framåt och diskutera med kunden.',
                name: 'gt_arsredovisning_2024.pdf',
                staticType: 'gt_arsredovisning_2025',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Huset' : 'The house',
                note:
                  'Med utgångspunkt från "Huset" kan du berätta om hur hållbarhetsarbetet bör arbetas in från ägare och ägardirektiv ner till den operativa verksamheten.',
                staticType: !langEN ? 'gt_huset' : 'gt_huset_en',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Kundundersökning' : 'Customer survey',
                note:
                  'Utgå från kundundersökningen för att visa att hållbarhetsfrågorna har fått ett ökat fokus och intresse av bolagen som vi frågade. Det har hänt mycket!"',
                staticType: !langEN
                  ? 'esg_kundundersokning'
                  : 'esg_kundundersokning_en',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Er situation' : 'Your situation',
          },
          {
            comment: '',

            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Nuläge' : 'Current situation',
                note: 'Ställ frågorna till kunden och lät dem reflektera',
                items: [
                  !langEN
                    ? 'Var vill ni vara om tre år?'
                    : 'Where do you want to be in three years?',
                  !langEN
                    ? 'Vilka är era tre största utmaningar?'
                    : 'What are your three biggest challenges?',
                  !langEN
                    ? 'Vilka är era tre viktigaste mål?'
                    : 'What are your three most important goals?',
                  '',
                  '',
                ],
              },
              {
                contentType: 'contentText',
                description: !langEN ? 'Lagkrav' : 'Legal requirements',
                note:
                  'Berätta om hur bolaget kan behöva rapportera hållbarhetsdata utan att träffas av lagkravet.',
                text: !langEN
                  ? 'Direkt\nTräffas ni redan idag eller i en nära framtid av lagstiftningen.\n\nIndirekt\nExempelvis att ni ingår i ett större bolags värdekedja där ni träffas av regelverk genom att det större bolaget ställer krav på arbete eller data för att de i sin tur skall kunna uppfylla lagstiftningen.\n\nHar ni finansiärer som ställer kvar på att ni arbetar med hållbarhet?\n\nAndra skäl\nHävda sig i konkurrensen, attrahera medarbetare m.m. Förväntningar som finns på bolaget.'
                  : 'Direct\nAre you already today or in the near future affected by the legislation.\n\nIndirect\nFor example, that you are part of a larger companys value chain where you are affected by regulations because the larger company demands work or data so that they in turn can comply with the legislation.\n\nDo you have financiers who demand that you work with sustainability?\n\nOther reasons\nAssert yourself in competition, attract employees etc. Expectations that exist on the company.',
              },
              {
                contentType: 'contentText',
                description: !langEN ? 'Värdekedja' : 'Value chain',
                note:
                  'Diskutera om de har större kunder eller leverantörer som kommer behöva samla in hållbarhetsdata framöver. Bra att undersöka och proaktivt ställa frågor till de kunder och leverantörer som kan vara aktuella.',
                text: !langEN
                  ? 'Har ni kunder eller leverantörer som kommer att ställa krav på er utifrån hållbarhetsperspektivet?\n\nDet kan vara allt från att ni skall redovisa era utsläpp till att ni skall ha en policy för hur ni arbetar med hållbarhet.\n\n'
                  : 'Do you have customers or suppliers who will demand you based on the sustainability perspective?\n\nIt can be anything from you reporting your emissions to you having a policy for how you work with sustainability.\n\n',
              },
            ],
          },
          {
            comment: '',
            note:
              'För de kunder som vi sköter löpande rapportering för ingår att löpande följa bolagets klimatavtryck.',
            slideType: 'slideHeadline',
            title: !langEN ? 'Klimatnyckeltal' : 'Climate key figures',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN ? 'Översikt' : 'Overview',
                note:
                  'Berätta att bolaget kan följa sitt klimatavtryck visuellt utifrån grafer och nyckeltal.',
                staticType: !langEN
                  ? '250922_Klimatnyckeltal'
                  : '250922_Klimatnyckeltal_EN',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Enkel' : 'Basic',
                note:
                  'Kan vara värt att lyfta att om bolaget har under 8000 verifikationer R12 ingår det i Budget&Rapport utan extra kostnad. Om de har över 8000 verifikationer; se prislista som finns på KNOW.',
                staticType: !langEN ? 'klimat_enkel' : 'klimat_enkel_en',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Utökad' : 'Extended',
                note:
                  'Berätta om vad som tillkommer om bolaget väljer den utökade',
                staticType: !langEN
                  ? '250930_Klimatnyckeltal_utokad'
                  : '250930_Klimatnyckeltal_utokad_EN',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Sociala nyckeltal' : 'Social key figures',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN
                  ? 'Sociala nyckeltal'
                  : 'Social key figures',
                staticType: !langEN
                  ? '250922_Sociala_nyckeltal'
                  : '250922_Sociala_nyckeltal_EN',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Nästa steg' : 'Next step',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Förslag' : 'Proposal',
                note:
                  'Diskutera med kunder hur de kan börja arbeta med hållbarhet. En bra och utmanande fråga är "Hur kan ni tjäna pengar på hållbarhet?"',
                items: [
                  !langEN
                    ? 'Identifiera era väsentliga hållbarhetsfrågor'
                    : 'Identify your essential sustainability issues',
                  !langEN ? 'Se över mål och KPI:er' : 'Review goals and KPIs',
                  !langEN
                    ? 'Informera internt och externt'
                    : 'Inform internally and externally',
                  !langEN
                    ? 'Viktigt att arbeta med rätt saker/aktiviteter'
                    : 'Important to work with the right things/activities',
                  '',
                ],
              },
              {
                contentType: 'contentList',
                description: !langEN ? 'Internt' : 'Internally',
                items: [
                  !langEN
                    ? 'Involvera berörda personer'
                    : 'Involve relevant people',
                  !langEN
                    ? 'Informera organisationen'
                    : 'Inform the organization',
                  !langEN ? 'Utbilda och inspirera' : 'Educate and inspire',
                  !langEN
                    ? 'Se över policys och komplettera med hållbarhet'
                    : 'Review policies and complement with sustainability',
                  '',
                ],
              },
              {
                contentType: 'contentList',
                description: !langEN ? 'Externt' : 'Externally',
                items: [
                  !langEN
                    ? 'Kommunicera genom att föra dialog och involvera...'
                    : 'Communicate by engaging in dialogue and involving...',
                  !langEN ? '...era kunder' : '...your customers',
                  !langEN ? '...era leverantörer' : '...your suppliers',
                  !langEN
                    ? '...eventuella finansiärer'
                    : '...possible financiers',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN ? 'Vi hjälper till' : 'We help',
                staticType: !langEN
                  ? 'esg_our_services'
                  : 'esg_our_services_en',
              },
              {
                contentType: 'contentImage',
                description: !langEN
                  ? 'Rådgivningstjänst'
                  : 'Consulting service',
                staticType: !langEN
                  ? '250922_Var_metod'
                  : '250922_Var_metod_EN',
              },
            ],
          },
          {
            comment: '',
            slideType: 'contentLiability',
          },
        ];
        break;
      case 'deck-esgkpi':
        slides = [
          {
            comment: '',
            slideType: 'slideTitle',
            subtitle: '',
            title: !langEN
              ? 'Klimat- och Sociala nyckeltal'
              : 'Climate and Social key figures',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: 'Agenda',
                items: [
                  !langEN
                    ? 'Genomgång GHG-protokollet'
                    : 'Review of the GHG protocol',
                  !langEN
                    ? 'Klimatnyckeltal i Budget & Rapport'
                    : 'Climate key figures in Budget & Report',
                  !langEN
                    ? 'Sociala nyckeltal i Budget & Rapport'
                    : 'Social key figures in Budget & Report',
                  !langEN
                    ? 'Hur ser det ut i ert bolag?'
                    : 'What does it look like in your company?',
                  !langEN ? 'Nästa steg' : 'Next step',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN
              ? 'Genomgång GHG-protokollet'
              : 'Review of the GHG protocol',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'GHG-protokollet' : 'GHG protocol',
                note:
                  'GHG står för Greenhouse Gas Protocol och är en internationell standard som används för att beräkna företag och organisationers klimpatpåverkan från växthusgasutsläpp, CO2 , det vill säga bolagets klimatavtryck.',
                items: [
                  !langEN
                    ? 'Internationell standard'
                    : 'International standard',
                  !langEN
                    ? 'Hur bolagets klimatavtryck beräknas och redovisas'
                    : 'How the companys climate footprint is calculated and reported',
                  !langEN
                    ? 'Utgår ifrån fem redovisningsprinciper'
                    : 'Based on five accounting principles',
                  !langEN
                    ? 'Delas in i tre olika scopes'
                    : 'Divided into three different scopes',
                  '',
                ],
              },
              {
                contentType: 'contentList',
                description: !langEN
                  ? 'Redovisningsprinciper'
                  : 'Accounting principles',
                note:
                  'De fem redovisningsprinciperna är; \nRelevans (beräkna på de källorna som är relevanta för bolagets verksamhet)\nFullständighet (Få en fullständig bild utifrån redovisade siffror)\nJämförbarhet (använda samma metodik) (kunna jämföra olika bolag med varandra samt samma bolag över tid)\nTransparens (vad har bolaget med i beräkningar och inte med i beräkningarna, i vilka fall har vi baserat på faktisk data och i vilka fall har bolaget beräknat på uppskattningar eller estimeringar)\nNoggrannhet och tillförlitlighet ( är så noggrann som möjligt, kan lita på det som redovisas)\n\nDessa principer innebär att den data som bolaget beräknas och redovisas skall uppfylla de fem kraven.\nHär kan ni berätta om alla eller ge exempel för att förtydliga för kunden vad en redovisningsprincip enligt GHG protokollet kan innebära',
                items: [
                  !langEN ? 'Relevans' : 'Relevance',
                  !langEN ? 'Fullständighet' : 'Completeness',
                  !langEN ? 'Jämförbarhet' : 'Comparability',
                  !langEN ? 'Transparens' : 'Transparency',
                  !langEN
                    ? 'Noggranhet & tillförlitlighet'
                    : 'Accuracy & reliability',
                ],
              },
              {
                contentType: 'contentList',
                description: 'Scopes',
                note:
                  'De tre scopen är:\nScope 1 Direkta utsläpp (från bolagets egna fordon, egna fabriker. Det vill säga som genereras av bolagets verksamhet)\nScope 2 Indirekta utsläpp (från energi, el, värme, kyla för att bedriva bolagets verksamhet. \nScope 3 Övriga indirekta utsläpp (den svåraste kategorin då det innehåller allt annat. Utsläpp från kunderna när de använder produkterna leverantörerna för sin tillverkning, eller från underleverantörer.)\n\nGe gärna exempel på de olika scopen.',
                items: [
                  !langEN
                    ? 'Scope 1 - Direkta utsläpp'
                    : 'Scope 1 - Direct emissions',
                  !langEN
                    ? 'Scope 2 - Indirekta utsläpp'
                    : 'Scope 2 - Indirect emissions',
                  !langEN
                    ? 'Scope 3 - Övriga indirekta utsläpp'
                    : 'Scope 3 - Other indirect emissions',
                  '',
                  '',
                ],
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Exempel' : 'Example',
                note:
                  'På fliken "Exempel" visas exempel på vad de olika scopen kan innehålla. Uppströms (leverantörsledet) / Nedströms (kundledet). Här ser man också och kan förklara för kunden att när man gör manuella beräkningar får man ofta bara med 2-3 kategorier i scope 3 medan i B&R tas allt med som man har faktura eller kvitto på.\n\nAtt kartlägga sin värdekedja är ett viktigt första steg i en dubbel väsentlighetsanalys (DMA). I detta steg identifierar vi verksamhetens huvudaktiviteter och intressenter - vilket utgör grunden för att i nästa steg kunna identifiera påverkan, risker och möjligheter(IROs).\n\nMed andra ord: det är utifrån de aktiviteter som kartläggs i värdekedjan som vi sedan analyserar var och hur er verksamhet har påverkan, står inför risker eller kan identifiera möjligheter.\n\nSom ni ser i bilden behöver man ta hänsyn till hela värdekedjan - både det som sker uppströms (före den egna verksamheten) och nedströms (efter den egna verksamheten).',
                staticType: !langEN
                  ? '250922_Vardekedja'
                  : '250922_Vardekedja_EN',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            // tslint:disable-next-line:max-line-length
            title: !langEN
              ? 'Klimatnyckeltal i Budget & Rapport'
              : 'Climate key figures in Budget & Rapport',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN ? 'Översikt' : 'Overview',
                note:
                  'Ordning på tolkning:\n1. Fakturabild, kvittobild\n2. Kontonummer\n3. Beskrivning kopplat till verifikationen\n\nFinns i två versioner: enkel och utökad\n 13 månader data har lästs in (om det finns i Fortnox). Blir mer detaljerat med den automatiska hanteringen mot att mäta klimatavtryck manuellt med frågor till bolaget. Genom att följa klimatnyckeltal i Budget & Rapport får bolaget en överblick över utsläppen och kan enklare förstå vart de skall fokusera sina insatser - där det ger störst avtryck.\n\nBeräkningar:\n- GHG-protokollet\n- Scope 1, 2 och 3\n\t- Baseras på bokföringen\n- Activty based\n\t- kwh\n\t- Kylvätskor\n\t- datorer, telefoner, skärmar \n\t- flyg \n -Spend based\n\t- resterande, men går över till activity based när möjligt\n- Kompletteras med användarinput\n\t- antal anställda (mer kommer inom kort)',
                staticType: !langEN
                  ? '250922_Klimatnyckeltal'
                  : '250922_Klimatnyckeltal_EN',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Enkel' : 'Basic',
                staticType: !langEN ? 'klimat_enkel' : 'klimat_enkel_en',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Utökad' : 'Extended',
                staticType: !langEN
                  ? '250930_Klimatnyckeltal_utokad'
                  : '250930_Klimatnyckeltal_utokad_EN',
              },
              {
                contentType: 'contentText',
                description: !langEN ? 'Fördelar' : 'Advantages',
                text: !langEN
                  ? 'Visualiserar företagets koldioxidutsläpp\n\nLöpande rapportering månadsvis\n\nUtgår ifrån information på leverantörsfakuror och kvitton (bilder)\n\nHämtar information från kontonamn och beskrivningar på verifikationer\n\nBeräkningar enligt GHG-protokollet\n\nAutomatiska beräkningar\n\nViss manuell kontroll innan månadens data släpps för bolaget'
                  : 'Visualizes the companys carbon dioxide emissions\n\nOngoing reporting monthly\n\nBased on information on supplier invoices and receipts (images)\n\nRetrieves information from account names and descriptions on verifications\n\nCalculations according to the GHG protocol\n\nAutomatic calculations\n\nSome manual control before the months data is released for the company',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            // tslint:disable-next-line:max-line-length
            title: !langEN
              ? 'Sociala nyckeltal i Budget & Rapport'
              : 'Social key figures in Budget & Rapport',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN
                  ? 'Sociala nyckeltal'
                  : 'Social key figures',
                staticType: !langEN
                  ? '250922_Sociala_nyckeltal'
                  : '250922_Sociala_nyckeltal_EN',
              },
            ],
          },
          {
            comment: '',
            note:
              'Gå in i Budget & Rapport och visa hur det ser ut för kunden.',
            slideType: 'slideHeadline',
            title: !langEN
              ? 'Hur ser det ut i ert bolag?'
              : 'What does it look like in your company?',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN
                  ? 'Er situation Klimat'
                  : 'Your situation Climate',
                items: [
                  !langEN
                    ? 'Hur går tankarna när ni sett hur status är i ert bolag kring klimatnyckeltal?'
                    : 'What are your thoughts when you have seen the status of your company regarding climate key figures?',
                  !langEN
                    ? 'Har ni mål idag på att minska klimatpåverkan?'
                    : 'Do you have goals today to reduce climate impact?',
                  !langEN
                    ? 'Har ni upprättat en policy på områden som vi såg har stor påverkan på klimatet?'
                    : 'Have you established a policy in areas that we saw have a major impact on the climate?',
                  '',
                  '',
                ],
              },
              {
                contentType: 'contentList',
                description: !langEN
                  ? 'Er situation Socialt'
                  : 'Your situation Social',
                items: [
                  !langEN
                    ? 'Hur jobbar ni med att utveckla era medarbetare?'
                    : 'How do you work with developing your employees?',
                  !langEN
                    ? 'Har ni en uppförandekod/arbetsmiljöpolicy?'
                    : 'Do you have a code of conduct/work environment policy?',
                  !langEN
                    ? 'Hur ser lång- och korttidsfrånvaron ut?'
                    : 'How does long- and short-term absence look?',
                  !langEN
                    ? 'Hur jobbar ni med underleverantörer?'
                    : 'How do you work with subcontractors?',
                  '',
                ],
                note:
                  'Medarbetar utgör en viktig del av ert värdeskapande, hur arbetar ni med att förebygga sjukfrånvaro, olyckor, personalomsättning och att utveckla era medarbetare?\n\nVad kostar det er per år? Hur arbetar ni med ledarskap och vidareutveckling? Hur arbetar ni med jämlikhet och jämställdhet? Hur arbetar ni med mångfald? Hur fångar ni upp diskriminering? Finns det ett visselblåsarsystem?\n\nHur arbetar ni med frågor som affärsetik, mutor, korruption, penningtvätt, jämställdhet, sexuella trakasserier, avfallshantering, vattenanvändning hos era leverantörer/underentreprenörer?',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentCategory: 'sustainability',
                contentType: 'contentLibrary',
                description: !langEN ? 'Intressenter' : 'Stakeholders',
                subjects: ['sustainabilityImpact2409'],
                note:
                  'Fliken har en interaktiv funktion där ni kan klicka på cirklarna för att få uppslag i mötet om varför intressenterna kan vara viktiga för kunden.',
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Huset' : 'The house',
                staticType: !langEN ? 'gt_huset' : 'gt_huset_en',
                note:
                  'Ni kan berätta om hur hållbarhetsarbetet bör arbetas in från ägare och ägardirektiv ner till den operativa verksamheten.',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Nuläge' : 'Current situation',
                items: [
                  !langEN
                    ? 'Var vill ni vara om tre år?'
                    : 'Where do you want to be in three years?',
                  !langEN
                    ? 'Vilka är era tre största utmaningar?'
                    : 'What are your three biggest challenges?',
                  !langEN
                    ? 'Vilka är era tre viktigaste mål?'
                    : 'What are your three most important goals?',
                  '',
                  '',
                ],
              },
              {
                contentType: 'contentImage',
                description: !langEN ? 'Styrning' : 'Leadership',
                staticType: !langEN
                  ? '250922_Hallbarhetspyramid'
                  : '250922_Hallbarhetspyramid',
                note:
                  'Styrning är en förutsättning för integrering och för att lyckas med ert bolags hållbarhetsarbete. Det behöver, som bilden visar, integreras i hela företaget och strategiskt.',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Nästa steg' : 'Next step',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Nästa steg' : 'Next step',
                items: [
                  !langEN
                    ? 'Förfina kvaliteten på data'
                    : 'Refine the quality of data',
                  !langEN
                    ? 'Analysera kunder & leverantörer'
                    : 'Analyze customers & suppliers',
                  '',
                  '',
                  '',
                ],
              },
              {
                contentType: 'contentText',
                description: !langEN
                  ? 'Förfina kvaliteten'
                  : 'Refine the quality',
                note:
                  'Om kunden använder FLEX: Kvitton från FLEX kan specificeras annars kommer de med som ospecificerade resekostnader och tolkas utifrån kontonamn eller beskrivningen. Finns möjlighet att PDF-rapport från Flex till bokföring.',
                text: !langEN
                  ? 'Förfina kvaliteten vid beräkning av klimatnyckeltal i Budget & Rapport\n\n- Be era leverantörer att specificera resekostnader utifrån flyg, tåg, taxi o.s.v.\n- Ju mer detaljerade fakturor och kvitton desto bättre beräkningar\n- Registrera antal anställda månadsvis'
                  : 'Refine the quality when calculating climate key figures in Budget & Report\n\n- Ask your suppliers to specify travel expenses based on flights, trains, taxis, etc.\n- The more detailed invoices and receipts, the better calculations\n- Register the number of employees monthly',
              },
              {
                contentType: 'contentText',
                description: !langEN ? 'Värdekedja' : 'Value chain',
                text: !langEN
                  ? 'Hur ser era kund- och leverantörsstrukturer ut?\n\nNi behöver redan nu analysera om ni har kunder, leverantörer eller finansiärer som kommer att begära hållbarhetsupplysningar, vilka hållbarhetsupplysningar som kommer att begäras in och hur dessa uppgifter skall tas fram.'
                  : 'What do your customer and supplier structures look like?\n\nYou need to analyze now if you have customers, suppliers or financiers who will request sustainability information, what sustainability information will be requested and how this information will be produced.',
              },
              {
                contentType: 'contentText',
                description: !langEN ? 'Förslag' : 'Proposal',
                text: !langEN
                  ? 'Förslag på hur ni kan gå vidare:\n\n- Identifiera var som är era väsentliga utsläppskategorier\n- Se över ert arbete med mål och KPI:er\n- Se över kunder, leverantörer och ev. finansiärer och agera\n- Involvera berörda personer\n- Informera organisationen\n- Utbilda och inspirera\n- Behöver ni se över eller skapa policys?'
                  : 'Proposal on how you can proceed:\n\n- Identify what are your essential emission categories\n- Review your work with goals and KPIs\n- Review customers, suppliers and possible financiers and act\n- Involve relevant people\n- Inform the organization\n- Educate and inspire\n- Do you need to review or create policies?',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentImage',
                description: !langEN ? 'Vi hjälper till' : 'We help',
                staticType: !langEN
                  ? 'esg_our_services'
                  : 'esg_our_services_en',
              },
              {
                contentType: 'contentImage',
                description: !langEN
                  ? 'Rådgivningstjänst'
                  : 'Consulting service',
                staticType: !langEN
                  ? '250922_Var_metod'
                  : '250922_Var_metod_EN',
              },
            ],
          },
          {
            comment: '',
            slideType: 'contentLiability',
          },
        ];
        break;
      case 'deck-company':
        slides = [
          {
            comment: '',
            slideType: 'slideTitle',
            title: !langEN ? 'Bolagspresentation' : 'Company presentation',
            subtitle: '',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: 'Agenda',
                items: [
                  !langEN ? 'Översikt' : 'Overview',
                  !langEN ? 'Ekonomi' : 'Financials',
                  '',
                  '',
                  '',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Översikt' : 'Overview',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Översikt' : 'Overview',
                items: [
                  !langEN
                    ? 'Grundat {{info-grundat-ar}}'
                    : 'Founded {{info-grundat-ar}}',
                  !langEN
                    ? 'Nettoomsättning {{pl-nettoomsattning}}'
                    : 'Net sales {{pl-nettoomsattning}}',
                  !langEN
                    ? '{{info-anstallda}}'
                    : '{{info-employees}}',
                  !langEN ? data.overview.BRANSCH_TEXT : '',
                  '',
                ],
              },
              {
                chart: 'compass',
                contentType: 'contentChart',
                note:
                  'En bolagskompass är ett pedagogiskt sätt att visualisera hur ett företag mår där man i sin enklaste form kan säga att en stor figur visar ett bra bolag medans en liten figur visar ett dåligt bolag.\n\nDe i figuren redovisade nyckeltalen har valts med omsorg för att omfatta en så stor del av resultat- och balansräkningen som möjligt.\n\nDet indexerade värdet på de olika nyckeltalsaxlarna visar vilken percentil som bolaget når för det aktuella nyckeltalet i jämförelse med samtliga svenska aktiebolag.\n\nExempelvis innebär ett indexerat värde på 70 att bolagets aktuella nyckeltal är högre än 70% av samtliga svenska aktiebolag (och således sämre än de återstående 30 procenten).\n\nDe fyra huvudaxlarnas värden baseras huvudsakligen på närliggande nyckeltal utifrån en rätt komplicerad formel med syftet att i lekmannatermer skapa ett helhetsintryck av bolaget.',
                description: !langEN ? 'Bolagskompass' : 'Business navigator',
              },
              !langEN
                ? {
                    contentType: 'contentText',
                    description: 'Verksamhetstext',
                    text: data.overview.ACTIVITY_TEXT,
                  }
                : [],
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: !langEN ? 'Ekonomi' : 'Financials',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Resultatöversikt' : 'Income overview',
                items: [
                  // tslint:disable-next-line:max-line-length
                  !langEN
                    ? 'Nettoomsättning {{pl-nettoomsattning}}'
                    : 'Net sales {{pl-nettoomsattning}}',
                  !langEN
                    ? 'Rörelseresultat {{pl-rorelseresultat}}'
                    : 'Operating profit {{pl-rorelseresultat}}',
                  !langEN
                    ? 'Rörelsemarginal {{kpi-rorelsemarginal}}'
                    : 'Operating margin {{kpi-rorelsemarginal}}',
                  '',
                  '',
                ],
              },
              {
                chart: 'turnover',
                contentType: 'contentChart',
                description: !langEN ? 'Nettoomsättning' : 'Net sales',
              },
              {
                chart: 'operatingMargin',
                contentType: 'contentChart',
                description: !langEN ? 'Rörelsemarginal' : 'Operating marginal',
              },
              {
                contentType: 'contentTable',
                description: !langEN ? 'Resultattabell' : 'Income table',
                table: 'profitloss',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN ? 'Balansöversikt' : 'Balance overview',
                items: [
                  // tslint:disable-next-line:max-line-length
                  !langEN
                    ? 'Balansomslutning {{bs-summa-tillgangar}}'
                    : 'Total assets {{bs-summa-tillgangar}}',
                  !langEN
                    ? 'Justerat Eget kapital {{bs-justerat-eget-kapital}}'
                    : 'Adjusted Equity capital {{bs-justerat-eget-kapital}}',
                  !langEN
                    ? 'Soliditet {{kpi-soliditet-heltal}}'
                    : 'Solidity {{kpi-soliditet-heltal}}',
                  '',
                  '',
                ],
              },
              {
                chart: 'totalAssets',
                contentType: 'contentChart',
                description: !langEN ? 'Balansomslutning' : 'Total assets',
              },
              {
                chart: 'solidity',
                contentType: 'contentChart',
                description: !langEN ? 'Soliditet' : 'Solidity',
              },
              {
                contentType: 'contentTable',
                description: !langEN ? 'Balanstabell' : 'Balance table',
                table: 'balance',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: !langEN
                  ? 'Likviditetsöversikt'
                  : 'Liquidity overview',
                items: [
                  // tslint:disable-next-line:max-line-length
                  !langEN
                    ? 'Kassa & bank {{bs-kassa-bank}}'
                    : 'Cash {{bs-kassa-bank}}',
                  !langEN
                    ? 'Likviditet {{kpi-likviditet-heltal}}'
                    : 'Quick ratio {{kpi-likviditet-heltal}}',
                  data.balance.count !== 1
                    ? !langEN
                      ? 'Totalt kassaflöde {{cf-arets}}'
                      : 'Total cash flow {{cf-arets}}'
                    : '',
                  '',
                  '',
                ],
              },
              {
                chart: 'cash',
                contentType: 'contentChart',
                description: !langEN ? 'Kassa & Bank' : 'Cash',
              },
              {
                chart: 'quickRatio',
                contentType: 'contentChart',
                description: !langEN ? 'Likviditet' : 'Quick ratio',
              },
              data.balance.count !== 1
                ? {
                    chart: 'totalCashflow',
                    contentType: 'contentChart',
                    description: !langEN
                      ? 'Totalt kassaflöde'
                      : 'Total cash flow',
                  }
                : [],
            ],
          },
          {
            comment: '',
            slideType: 'contentLiability',
          },
        ];
        break;
      case 'deck-salesmeeting': {
        const companyName =
          data && data.overview && data.overview.NAME ? data.overview.NAME : '';
        const aboutSlide = getSlideByTemplate({
          template: 'productAboutGT',
          orgNr,
          sieAggVersion,
          data,
        });
        const resultSlide =
          data &&
          data.profitloss &&
          data.profitloss.sheets &&
          data.profitloss.sheets[0]
            ? getSlideByTemplate({
                template: 'profitlossOverview',
                orgNr,
                sieAggVersion,
                data,
              })
            : {
                comment: '',
                slideType: 'slideContent',
                template: 'profitlossOverview',
                subSlides: [
                  {
                    contentType: 'contentList',
                    description: 'Resultatöversikt',
                    items: ['', '', '', '', ''],
                  },
                ],
              };
        slides = [
          {
            comment: '',
            customSubtitle: false,
            slideType: 'slideTitle',
            subtitle: companyName,
            title: 'Möte med',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentList',
                description: 'Agenda',
                items: [
                  'Lära känna',
                  'Er berättelse',
                  'Vad vi sett',
                  'Vad kan vi göra för dig?',
                  'Nästa steg',
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideFullContent',
            subSlides: [
              {
                contentType: 'contentTeam',
                description: 'Vi i mötet',
                title: 'Vi i mötet',
                teamMembers: [],
              },
            ],
          },
          aboutSlide,
          {
            comment: '',
            slideType: 'slideHeadline',
            title: 'Er berättelse',
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: 'Vad vi sett',
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                chart: 'compass',
                contentType: 'contentChart',
                description: 'Flerårsutveckling',
                OrgNr: orgNr !== '00' ? orgNr : '',
                SieAggVersion: sieAggVersion,
              },
            ],
          },
          resultSlide,
          ...(user.user && user.user.AiInsightsEnabled === true
            ? [
                {
                  comment: '',
                  slideType: 'slideFullContent',
                  subSlides: [
                    {
                      contentType: 'contentAiInsights',
                      description: 'Insikter',
                      title: 'Insikter',
                      OrgNr: orgNr !== '00' ? orgNr : '',
                      selectedInsightIds: [],
                      insights: [],
                    },
                  ],
                },
              ]
            : []),
          {
            comment: '',
            slideType: 'slideHeadline',
            title: 'Vad kan vi göra för dig?',
          },
          {
            comment: '',
            slideType: 'slideFullContent',
            subSlides: [
              {
                contentType: 'contentTree',
                collectionId: 'services',
                startNodeId: 'services',
                description: 'Våra tjänster',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideFullContent',
            subSlides: [
              {
                contentType: 'contentTree',
                collectionId: 'grant-thornton-360',
                startNodeId: 'grant-thornton-360',
                description: 'Grant Thornton 360',
              },
            ],
          },
          {
            comment: '',
            slideType: 'slideHeadline',
            title: 'Nästa steg',
          },
          {
            slideType: 'contentNextSteps',
            starred: [],
          },
          {
            comment: '',
            slideType: 'slideContent',
            subSlides: [
              {
                contentType: 'contentSchedule',
                description: 'Nästa steg',
                items: [
                  { Text: '', date: null, text: '' },
                  { Text: '', date: null, text: '' },
                  { Text: '', date: null, text: '' },
                  { Text: '', date: null, text: '' },
                  { Text: '', date: null, text: '' },
                  { Text: '', date: null, text: '' },
                  { Text: '', date: null, text: '' },
                ],
              },
            ],
          },
          {
            comment: '',
            slideType: 'contentLiability',
          },
        ];
        break;
      }
    }

    const deck = {
      Type: type,
      Template: template,
      Name: name,
      Title: title,
      OrgNr: orgNr,
      SieAggVersion: sieAggVersion,
      BasedOn: basedOn,
      Slides: slides,
      Language: langEN ? 'en' : 'se',
    };
    return await api.addDocument(deck);
  }

  @Action
  async loadDoc(docWrapper: any) {
    const doc = docWrapper.Data;
    this.setLoaded({
      Type: doc.Type,
      Template: doc.Template,
      DocumentId: docWrapper.Id,
      OrgNr: doc.OrgNr,
      Name: doc.Name,
      Title: doc.Title,
      Slides: doc.Slides,
      SieAggVersion: doc.SieAggVersion || 0,
      BasedOn: doc.BasedOn || '',
      UserId: doc.AggId,
      AggVersion: doc.AggVersion,
      Background: doc.Background || 'dark',
      Language: doc.Language,
      NextStepsStarred: doc.NextStepsStarred || [],
      Editable: false,
    });

    this.setCurrentSlide(1);

    this.clearData();
    await this.reloadData(false);
  }

  @Action
  async loadShared({ blobId }: { blobId: string }) {
    this.setCurrentSlide(0);
    this.clearData();
    const res = await api.getSharedDocument(blobId);
    if (res) {
      this.setLoaded({
        Type: res.doc.Type,
        Template: res.doc.Template,
        DocumentId: res.doc.DocumentId,
        OrgNr: res.doc.OrgNr,
        Name: res.doc.Name,
        Title: res.doc.Title,
        Slides: res.doc.Slides,
        SieAggVersion: res.doc.SieAggVersion,
        BasedOn: res.doc.BasedOn,
        UserId: res.doc.AggId,
        AggVersion: res.doc.AggVersion,
        Background: res.doc.Background || 'dark',
        Language: res.doc.Language,
        NextStepsStarred: res.doc.NextStepsStarred || [],
        Editable: false,
      });
      for (const [industry, data] of Object.entries(res.industryRankings)) {
        this.setIndustryData({ industry, data });
      }
      for (const [orgNr, data] of Object.entries(res.data)) {
        this.setData({
          orgNr,
          sieAggVersion: (data as any).SieAggVersion,
          data,
        });
      }
    } else {
      console.log('Error loading shared document', blobId);
    }
  }

  @Action
  async loadFull({
    blobId,
    internal,
    editable,
  }: {
    blobId: string;
    internal: boolean;
    editable: boolean;
  }) {
    await this.load({ blobId, internal, editable });
    await this.reloadData(internal);
  }

  @Action
  async load({
    blobId,
    internal,
    editable,
  }: {
    blobId: string;
    internal: boolean;
    editable: boolean;
  }) {
    const doc = await api.getDocument(blobId, internal);
    const documentId = doc.DocumentId || blobId;
    this.setLoaded({
      Type: doc.Type,
      Template: doc.Template,
      DocumentId: documentId,
      OrgNr: doc.OrgNr,
      Name: doc.Name,
      Title: doc.Title,
      Slides: doc.Slides,
      SieAggVersion: doc.SieAggVersion || 0,
      BasedOn: doc.BasedOn || '',
      UserId: doc.AggId,
      AggVersion: doc.AggVersion,
      Background: doc.Background || 'dark',
      Language: doc.Language,
      NextStepsStarred: doc.NextStepsStarred || [],
      Editable: editable,
    });

    this.setCurrentSlide(1);

    await this.clearData();
  }

  @Action
  async reloadData(internal: boolean) {
    await this.loadData({
      orgNr: this.OrgNr,
      sieAggVersion: this.SieAggVersion,
      internal,
      branch: undefined,
    });
    for (const slide of this.Slides) {
      if (!slide.subSlides) {
        continue;
      }
      for (const subSlide of slide.subSlides) {
        if (subSlide.OrgNr) {
          await this.loadData({
            orgNr: subSlide.OrgNr,
            sieAggVersion: subSlide.SieAggVersion,
            internal,
            branch: subSlide.Branch,
          });
        } else if (subSlide.Branch) {
          await this.loadData({
            orgNr: this.OrgNr,
            sieAggVersion: this.SieAggVersion,
            internal,
            branch: subSlide.Branch,
          });
        }
        if (subSlide.competitor1) {
          await this.loadData({
            orgNr: subSlide.competitor1,
            sieAggVersion: 0,
            internal,
          });
        }
        if (subSlide.competitor2) {
          await this.loadData({
            orgNr: subSlide.competitor2,
            sieAggVersion: 0,
            internal,
          });
        }
      }
    }
  }

  @Action
  async save(): Promise<string> {
    const deck = {
      Type: this.Type,
      Template: this.Template,
      DocumentId: this.DocumentId,
      Name: this.Name,
      Title: this.Title,
      OrgNr: this.OrgNr,
      Slides: this.Slides,
      SieAggVersion: this.SieAggVersion,
      BasedOn: this.BasedOn,
      Background: this.Background,
      Language: this.Language,
      NextStepsStarred: this.NextStepsStarred,
    };
    const updated = JSON.stringify(deck);

    if (updated === this.original) {
      return Promise.resolve(this.DocumentId);
    } else {
      this.setOriginal(updated);
      return api.updateDocument(deck);
    }
  }

  @Action
  async isDirty(): Promise<boolean> {
    const deck = {
      Type: this.Type,
      Template: this.Template,
      DocumentId: this.DocumentId,
      Name: this.Name,
      Title: this.Title,
      OrgNr: this.OrgNr,
      Slides: this.Slides,
      SieAggVersion: this.SieAggVersion,
      BasedOn: this.BasedOn,
      Background: this.Background,
      Language: this.Language,
      NextStepsStarred: this.NextStepsStarred,
    };
    return this.original !== JSON.stringify(deck);
  }

  @Mutation
  setLoaded(loaded: any) {
    // Work on a clone so the deck store never mutates the caller's object.
    // Callers like MetaDeck.vue pass a deep-watched `doc.Data.Slides` by
    // reference; mutating it here (normalizeSlides / applyStarredToState)
    // retriggers the watcher and causes an infinite load loop.
    const slides = loaded.Slides
      ? JSON.parse(JSON.stringify(loaded.Slides))
      : loaded.Slides;
    if (slides) {
      normalizeSlides(slides);
      ensureLiabilityIsLast(slides);
      if (!hasContentTreeSlide(slides)) {
        const withoutNextSteps = slides.filter(
          (slide: any) => slide.slideType !== 'contentNextSteps',
        );
        slides.splice(0, slides.length, ...withoutNextSteps);
      }
    }
    Object.assign(this, { Type: loaded.Type });
    Object.assign(this, { Template: loaded.Template });
    Object.assign(this, { DocumentId: loaded.DocumentId });
    Object.assign(this, { UserId: loaded.UserId });
    Object.assign(this, { AggVersion: loaded.AggVersion });
    Object.assign(this, { OrgNr: loaded.OrgNr });
    Object.assign(this, { Name: loaded.Name });
    Object.assign(this, { Title: loaded.Title });
    Object.assign(this, { Slides: slides });
    Object.assign(this, { SieAggVersion: loaded.SieAggVersion });
    Object.assign(this, { BasedOn: loaded.BasedOn });
    Object.assign(this, { Background: loaded.Background });
    Object.assign(this, { nav: { currentSlide: 1, currentSubSlide: 1 } });
    Object.assign(this, { Language: loaded.Language });
    Object.assign(this, { Editable: loaded.Editable });
    const starred = hasContentTreeSlide(this.Slides || [])
      ? mergeStarredSources(this.Slides, loaded.NextStepsStarred)
      : [];
    applyStarredToState(this, starred);

    const deck = {
      Type: this.Type,
      Template: this.Template,
      DocumentId: this.DocumentId,
      Name: this.Name,
      Title: this.Title,
      OrgNr: this.OrgNr,
      Slides: this.Slides,
      SieAggVersion: this.SieAggVersion,
      BasedOn: this.BasedOn,
      Background: this.Background,
      Language: this.Language,
      NextStepsStarred: this.NextStepsStarred,
    };
    this.original = JSON.stringify(deck);
  }

  @Mutation
  setOriginal(original: string) {
    this.original = original;
  }

  @Action({ rawError: true })
  async loadData({
    orgNr,
    sieAggVersion,
    internal,
    branch,
  }: {
    orgNr: string;
    sieAggVersion: number;
    internal: boolean;
    branch?: string;
  }) {
    if (branch && !this.IndustryData[branch]) {
      const industry = await api.getIndustryRanking(branch);
      this.setIndustryData({ industry: branch, data: industry });
    }

    if (!hasCompanyOrgNr(orgNr)) {
      return;
    }
    if (this.Data[orgNr] && this.Data[orgNr][sieAggVersion]) {
      return;
    }

    const data = await getData({
      orgNr,
      sieAggVersion,
      internal,
      userId: this.UserId,
      version: this.Editable ? user.version : this.AggVersion,
    });

    this.setData({ orgNr, sieAggVersion, data });
  }

  @Mutation
  clearData() {
    Object.assign(this, { Data: { version: 0 } });
  }

  @Mutation
  setData({
    orgNr,
    sieAggVersion,
    data,
  }: {
    orgNr: string;
    sieAggVersion: number;
    data: any;
  }) {
    const x: any = this.Data;
    x[orgNr] = x[orgNr] || {};
    x[orgNr][sieAggVersion] = data;
    Object.assign(this, { Data: x });
    Vue.set(this.Data, 'version', x.version + 1);

    if (this.OrgNr === orgNr) {
      this.OrgName = data.overview.NAME;
    }
  }

  @Mutation
  setIndustryData({ industry, data }: { industry: string; data: any }) {
    const x: any = this.IndustryData;
    x[industry] = data;
    Object.assign(this, { IndustryData: x });
    Vue.set(this.IndustryData, 'version', x.version + 1);
  }

  @Mutation
  addSlide(slide: any) {
    if (this.nav.currentSlide === 0) {
      this.nav.currentSlide = 1;
    }
    if (this.nav.currentSlide === this.Slides.length) {
      this.nav.currentSlide = this.Slides.length - 1;
    }
    this.Slides.splice(this.nav.currentSlide, 0, slide);
  }

  @Mutation
  moveSlideRight(index: number) {
    const item1 = this.Slides[index];
    const item2 = this.Slides[index + 1];
    Vue.set(this.Slides, index, item2);
    Vue.set(this.Slides, index + 1, item1);
  }

  @Mutation
  moveSlideLeft(index: number) {
    const item1 = this.Slides[index];
    const item2 = this.Slides[index - 1];
    Vue.set(this.Slides, index, item2);
    Vue.set(this.Slides, index - 1, item1);
  }

  @Mutation
  deleteSlide(slide: number) {
    this.Slides.splice(slide, 1);
    syncNextStepsWithContentTree(this);
    ensureLiabilityIsLast(this.Slides);
    if (this.nav.currentSlide > this.Slides.length) {
      this.nav.currentSlide = this.Slides.length;
    }
  }

  @Action
  async updateSlideWithReload({ slide, index }: { slide: any; index: number }) {
    this.setIsLoading(true);
    try {
      this.updateSlide({ slide, index });
      await this.reloadData(false);
    } finally {
      this.setIsLoading(false);
    }
  }

  @Mutation
  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @Mutation
  updateSlide({ slide, index }: { slide: any; index: number }) {
    // console.log('updateSlide', slide, index);
    if (slide.slideType === 'slideTitle') {
      this.Title = slide.title;
    }
    Vue.set(this.Slides, index, slide);
    syncNextStepsWithContentTree(this);
  }

  @Mutation
  setBackground(background: string) {
    Object.assign(this, { Background: background });
  }

  @Mutation
  setName(name: string) {
    Object.assign(this, { Name: name });
  }

  @Mutation
  applyNextStepsStarred(starred: NextStepStarredItem[]) {
    applyStarredToState(this, starred);
  }

  @Mutation
  toggleNextStepStar({
    collectionId,
    leafId,
    name,
  }: {
    collectionId: string;
    leafId: string;
    name: string;
  }) {
    const key = collectionId + ':' + leafId;
    const starred = this.NextStepsStarred.slice();
    const index = starred.findIndex(
      (item) => item.collectionId + ':' + item.leafId === key,
    );
    if (index >= 0) {
      starred.splice(index, 1);
    } else {
      starred.push({ collectionId, leafId, name });
    }
    applyStarredToState(this, starred);
  }

  @Action
  async toggleAndSaveNextStepStar({
    collectionId,
    leafId,
    name,
  }: {
    collectionId: string;
    leafId: string;
    name: string;
  }) {
    this.toggleNextStepStar({ collectionId, leafId, name });
    await this.save();
  }

  @Mutation
  setNextStepsEnabled(enabled: boolean) {
    const hasSlide = this.Slides.some(
      (slide) => slide.slideType === 'contentNextSteps',
    );
    if (enabled) {
      if (!hasSlide) {
        const nextStepsSlide = {
          slideType: 'contentNextSteps',
          starred: this.NextStepsStarred.slice(),
        };
        const insertAt = findInsertIndexAfterServices(
          this.Slides,
          this.nav.currentSlide,
        );
        this.Slides.splice(insertAt, 0, nextStepsSlide);
        ensureLiabilityIsLast(this.Slides);
      }
      return;
    }
    if (hasSlide) {
      removeNextStepsFromState(this);
    }
  }

  @Action
  async setNextStepsEnabledAndSave(enabled: boolean) {
    this.setNextStepsEnabled(enabled);
    await this.save();
  }

  @Mutation
  setCurrentSlide(item: number) {
    this.nav.currentSlide = item;
    this.nav.currentSubSlide = 0;
  }

  @Mutation
  setCurrentSubSlide(item: number) {
    this.nav.currentSubSlide = item;
  }

  @Mutation
  async setSlideByTemplate({
    template,
    orgNr,
    sieAggVersion,
    index,
  }: {
    template: string;
    orgNr: string;
    sieAggVersion: number;
    index: number;
  }) {
    sieAggVersion = sieAggVersion || this.SieAggVersion;
    orgNr = orgNr || this.OrgNr;

    let data: any = {};
    if (orgNr !== '00') {
      data = this.Data[orgNr][sieAggVersion];
    }

    const slide = getSlideByTemplate({ template, orgNr, sieAggVersion, data });

    Vue.set(this.Slides, index, slide);
  }
}

export default getModule(DeckModule);
